/**
 * The page template a URL renders (web plan W5.2): the grouping for real-user
 * Web Vitals in GA4, so reports compare "brand pages" with "promotion pages"
 * rather than thousands of single URLs. A template is a fixed word, never a
 * path, an id or a slug.
 *
 * Read from the route list the SPA's router and the SEO server share
 * (`@/routes/legacyRoutes`), under both states of the URL-move switch, so
 * `/brand/khaadi` and `/pk/en/brand/khaadi` are both `brand` whichever build
 * this is. Literal segments compare case-insensitively and a `:param` takes
 * one non-empty segment, as React Router and the SEO server match them.
 */
import { LEGACY_ROUTES, localeRoutesFor, type AppRoute, type RoutePage } from '@/routes/legacyRoutes';
import { LOCALES } from '@/seo/locales';

export type PageTemplate = 'home' | 'list' | 'brand' | 'promotion' | 'store' | 'sale' | 'hub' | 'other';

/**
 * - `list`: the paginated catalog lists (/brands, /shops, /promotions).
 * - `store`: store AND shop pages — the same kind of page (one physical place,
 *   its address and map, its promotions).
 * - `sale`: the sale calendar and the sale-event hubs.
 * - `hub`: the landing hubs (category hubs, web plan W3.1).
 * - `other`: search, nearby, account, sign-in, legal and company pages and
 *   anything unknown (the 404 page).
 */
const TEMPLATE_OF_PAGE: Record<RoutePage, PageTemplate> = {
  home: 'home',
  localeHome: 'home',
  countryHome: 'home',
  brands: 'list',
  shops: 'list',
  promotions: 'list',
  brand: 'brand',
  promotion: 'promotion',
  store: 'store',
  shop: 'store',
  saleCalendar: 'sale',
  saleEvent: 'sale',
  category: 'hub',
  city: 'hub',
  'brand-city': 'hub',
  search: 'other',
  nearby: 'other',
  favorites: 'other',
  notifications: 'other',
  notificationSettings: 'other',
  profile: 'other',
  settings: 'other',
  feedback: 'other',
  privacy: 'other',
  mobilePrivacy: 'other',
  deleteAccount: 'other',
  terms: 'other',
  login: 'other',
  register: 'other',
  verifyOtp: 'other',
  confirmEmail: 'other',
  forgotPassword: 'other',
  resetPassword: 'other',
  // The company (trust) pages, web plan W3.7.
  about: 'other',
  contact: 'other',
  howWeVerify: 'other',
  forBusinesses: 'other',
};

interface CompiledRoute {
  page: RoutePage;
  segments: string[];
}

let table: CompiledRoute[] | null = null;

/** Every route, most literal segments first (a literal beats a `:param`). */
function routeTable(): CompiledRoute[] {
  if (table) return table;
  const routes: AppRoute[] = [
    ...LEGACY_ROUTES,
    ...LOCALES.flatMap((locale) => [...localeRoutesFor(locale, false), ...localeRoutesFor(locale, true)]),
  ];
  const literals = (segments: string[]) => segments.filter((s) => !s.startsWith(':')).length;
  table = routes
    .map((route) => ({ page: route.page, segments: route.path.split('/').filter(Boolean) }))
    .sort((a, b) => literals(b.segments) - literals(a.segments));
  return table;
}

function pathnameOf(url: string): string | null {
  try {
    return new URL(url, 'https://promostork.com').pathname;
  } catch {
    return null;
  }
}

/** The template of `url` (absolute, or a path with or without a query). */
export function pageTemplate(url: string): PageTemplate {
  const pathname = pathnameOf(url);
  if (pathname === null) return 'other';
  const segments = pathname.split('/').filter(Boolean);
  for (const route of routeTable()) {
    if (route.segments.length !== segments.length) continue;
    const matches = route.segments.every((pattern, i) =>
      pattern.startsWith(':') ? true : pattern.toLowerCase() === segments[i].toLowerCase(),
    );
    if (matches) return TEMPLATE_OF_PAGE[route.page];
  }
  return 'other';
}
